import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axios";
import { Sidebar } from "../sidebar/Sidebar";
import { Navbar } from "../navbar/Navbar";
import { toast } from "react-toastify";
import { useLocation } from 'react-router-dom';

import "./projectParticularForm.scss";

const ViewProjectParticular = () => {
  const location = useLocation();
  const [formData, setFormData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [studentName, setStudentName] = useState("");
  const [mentor2Enabled, setMentor2Enabled] = useState(false);
  const {reportType,submissionDate} = location.state || "";
  const studentId = localStorage.getItem("studentId");

  const deadline = new Date(`${submissionDate}T23:59:59`);
  const now = new Date();

  const isDisabled =
    reportType !== "ProjectParticular"
      ? true                          
      : now >= deadline; 

  useEffect(() => {
    const name = localStorage.getItem("username");
    if (name) setStudentName(name);

    axiosInstance
      .get(`/project-particular/${studentId}`)
      .then((res) => {
        const { thesis, mentor1, mentor2 } = res.data.data;

        const mentor2Exists =
          !!(mentor2 && (mentor2.id || Object.values(mentor2).some(v => v !== null && String(v).trim() !== "")));

        setFormData({
          thesisId: thesis.id,
          studentId: thesis.studentId,
          title: thesis.title,
          thesisAbstract: thesis.thesisAbstract,
          keywords: thesis.keywords,
          mentorId1ReadyViva: thesis.mentorId1ReadyViva,
          mentorId2ReadyViva: thesis.mentorId2ReadyViva || null,
          immediateSupervisorName: thesis.immediateSupervisorName,
          immediateSupervisorContact: thesis.immediateSupervisorContact,
          instructor1: {
            id: mentor1?.id,
            name: mentor1?.name,
            email: mentor1?.email,
            phoneNumber: mentor1?.phoneNumber || "",
            organization: mentor1?.organization,
            totalWorkExperience: mentor1?.totalWorkExperience || "",
            totalAIWorkExperience: mentor1?.totalAIWorkExperience || "",
            linkedinProfile: mentor1?.linkedinProfile || "",
            cv: mentor1?.cv || "",
          },
          instructor2: mentor2Exists
            ? {
                id: mentor2?.id || "",
                name: mentor2?.name || "",
                email: mentor2?.email || "",
                phoneNumber: mentor2?.phoneNumber || "",
                organization: mentor2?.organization || "",
                totalWorkExperience: mentor2?.totalWorkExperience || "",
                totalAIWorkExperience: mentor2?.totalAIWorkExperience || "",
                linkedinProfile: mentor2?.linkedinProfile || "",
                cv: mentor2?.cv || "",
              }
            : {
                id: "",
                name: "",
                email: "",
                phoneNumber: "",
                organization: "",
                totalWorkExperience: "",
                totalAIWorkExperience: "",
                linkedinProfile: "",
                cv: "",
              },
        });

        setMentor2Enabled(Boolean(mentor2Exists));
      })
      .catch(() => {
        toast.error("Failed to fetch project particulars");
      });
  }, [studentId]);

  const isNonEmptyString = (v) => typeof v === "string" && v.trim() !== "";
  const isNonEmptyNumber = (v) => {
  const num = Number(v);
  return v !== "" && !isNaN(num) && num >= 0;
};
  const toNullIfEmpty = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === "string") {
      const t = v.trim();
      return t === "" ? null : t;
    }
    return v;
  };
  const isValidEmail = (email) =>
    typeof email === "string" && /\S+@\S+\.\S+/.test(email.trim());

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("instructor1.")) {
      const key = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        instructor1: {
          ...prev.instructor1,
          [key]: value,
        },
      }));
    } else if (name.startsWith("instructor2.")) {
      const key = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        instructor2: {
          ...prev.instructor2,
          [key]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const clearMentor2 = () => {
    setFormData((prev) => ({
      ...prev,
      instructor2: {
        id: "",
        name: "",
        email: "",
        phoneNumber: "",
        organization: "",
        totalWorkExperience: "",
        totalAIWorkExperience: "",
        linkedinProfile: "",
        cv: "",
      },
      mentorId2ReadyViva: null,
    }));
    setMentor2Enabled(false);
    setEditing(true);
  };

  const enableMentor2 = () => {
    setMentor2Enabled(true);
    setEditing(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!formData) return;

    // ---------- Mentor1 validation ----------
    const m1 = formData.instructor1 || {};
    const aiExp = parseFloat(m1.totalAIWorkExperience || "0");
    const m1HasLinkedIn = isNonEmptyString(m1.linkedinProfile);
    const m1HasCv = isNonEmptyString(m1.cv);
    if (!m1HasLinkedIn && !m1HasCv) {
      toast.error("Mentor 1 must have either LinkedIn profile or CV");
      return;
    }

    if (aiExp === 0 && !mentor2Enabled) {
      toast.error("Mentor 2 is required if Mentor 1 has 0 years of AI/ML experience");
      return;
    }

    // ---------- Mentor2 validation (only if enabled) ----------
    if (mentor2Enabled) {
      const m2 = formData.instructor2 || {};

      // require name
      if (!isNonEmptyString(m2.name)) {
        toast.error("Please provide the name of Mentor 2");
        return;
      }

      // require email if student wants to add mentor 2
      if (!isValidEmail(m2.email || "")) {
        toast.error("Please provide a valid email for Mentor 2");
        return;
      }

      // require company/organization
      if (!isNonEmptyString(m2.organization)) {
        toast.error("Please provide the company/organization of Mentor 2");
        return;
      }

      // require total experience
      if (!isNonEmptyNumber(m2.totalWorkExperience)) {
        toast.error("Please provide total work experince of Mentor 2");
        return;
      }

      // require total ai work experience
      if (!isNonEmptyNumber(m2.totalAIWorkExperience)) {
        toast.error("Please provide total AI work experince of Mentor 2");
        return;
      }


      const m2HasLinkedIn = isNonEmptyString(m2.linkedinProfile);
      const m2HasCv = isNonEmptyString(m2.cv);
      if (!m2HasLinkedIn && !m2HasCv) {
        toast.error("Mentor 2 must have either LinkedIn profile or CV if you provide their details");
        return;
      }
    }

    // ---------- build payload ----------
    // normalize mentor1 values (trim)
    const mentor1Payload = {};
    Object.entries(formData.instructor1 || {}).forEach(([k, v]) => {
      mentor1Payload[k] = toNullIfEmpty(v);
    });

    let mentor2Payload = null;
    if (mentor2Enabled) {
      mentor2Payload = {};
      Object.entries(formData.instructor2 || {}).forEach(([k, v]) => {
        mentor2Payload[k] = toNullIfEmpty(v);
      });
    }

    const payload = {
      thesis: {
        id: formData.thesisId,
        title: formData.title,
        thesisAbstract: formData.thesisAbstract,
        keywords: formData.keywords,
        mentorId1ReadyViva: formData.mentorId1ReadyViva,
        mentorId2ReadyViva: formData.mentorId2ReadyViva,
        immediateSupervisorName: formData.immediateSupervisorName,
        immediateSupervisorContact: formData.immediateSupervisorContact,
        mentorId1: formData.instructor1?.id,
        mentorId2: mentor2Enabled ? (formData.instructor2?.id || null) : null,
      },
      mentor1: mentor1Payload,
      mentor2: mentor2Payload,
    };

    try {
      await axiosInstance.put("/project-particular", payload);
      toast.success("Project particulars updated successfully");
      setEditing(false);
    } catch (error) {
      console.error(error);
      toast.error("Update failed");
    }
  };

  if (!formData) return <div>Loading...</div>;

  const mentorFields = [
    "name",
    "email",
    "phoneNumber",
    "organization",
    "totalWorkExperience",
    "totalAIWorkExperience",
    "linkedinProfile",
    "cv",
  ];

  const mentorFieldLabels = {
    name: "Name",
    email: "Email",
    phoneNumber: "Phone Number",
    organization: "Company",
    totalWorkExperience: "Experience (Years) in your Problem Domain",
    totalAIWorkExperience: "Experience (Years) in AI/ML Field",
    linkedinProfile: "LinkedIn Profile",
    cv: "CV Link",
  };

  return (
    <div className="projectParticularForm">
      <Sidebar />
      <div className="mainContainer">
        <Navbar />
        <div className="formContainer">
          <h2>Submitted Project Particulars</h2>
          <form onSubmit={handleUpdate}>
            <input type="text" value={studentName} readOnly />

            <label>Project Title*</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              readOnly={!editing}
              required={editing}
            />

            <label>Project Abstract - A 150 words brief*</label>
            <textarea
              name="thesisAbstract"
              value={formData.thesisAbstract}
              onChange={handleChange}
              readOnly={!editing}
              required={editing}
            />

            <label>Keywords: Domain, AI/ML models and Approach*</label>
            <input
              type="text"
              name="keywords"
              value={formData.keywords}
              onChange={handleChange}
              readOnly={!editing}
              required={editing}
            />

            <label>Immediate Supervisor : Name *</label>
            <input
              type="text"
              name="immediateSupervisorName"
              value={formData.immediateSupervisorName}
              onChange={handleChange}
              readOnly={!editing}
              required={editing}
            />

            <label>Immediate Supervisor : Contact Details</label>
            <input
              type="text"
              name="immediateSupervisorContact"
              value={formData.immediateSupervisorContact}
              onChange={handleChange}
              readOnly={!editing}
              required={editing}
            />

            <h3>Mentor 1</h3>
            {mentorFields.map((field) => (
              <React.Fragment key={`instructor1.${field}`}>
                <label>
                  {mentorFieldLabels[field] || field.replace(/([A-Z])/g, " $1")}
                </label>
                <input
                  type="text"
                  name={`instructor1.${field}`}
                  value={formData.instructor1[field] || ""}
                  onChange={handleChange}
                  readOnly={!editing}
                  required={editing && field !== "phoneNumber" && field !== "linkedinProfile" && field !== "cv"}
                />
              </React.Fragment>
            ))}

            <label>Is Mentor 1 Ready to attend the Viva ?</label>
            <select
              name="mentorId1ReadyViva"
              value={formData.mentorId1ReadyViva || ""}
              onChange={handleChange}
              disabled={!editing}
              required={editing}
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="maybe">Maybe</option>
            </select>

            <h3 style={{ marginTop: 20 }}>Mentor 2</h3>

            {/* Add Mentor 2 button / clear button */}
            {!mentor2Enabled ? (
              <div style={{ marginBottom: 10 }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={enableMentor2}
                  disabled={isDisabled}
                  style={{cursor:isDisabled?"not-allowed" :"pointer"}}
                >
                  Add Mentor 2
                </button>
                <div style={{ color: "gray", marginTop: 6 }}>
                  Click "Add Mentor 2" to enter details.
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 10 }}>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={clearMentor2}
                  disabled={isDisabled}
                  style={{cursor:isDisabled?"not-allowed" :"pointer"}}
                >
                  Remove Mentor 2 (clear details)
                </button>
              </div>
            )}

            {/* only render Mentor2 fields when enabled */}
            {mentor2Enabled &&
              mentorFields.map((field) => (
                <React.Fragment key={`instructor2.${field}`}>
                  <label>
                    {mentorFieldLabels[field] || field.replace(/([A-Z])/g, " $1")}
                  </label>
                  <input
                    type="text"
                    name={`instructor2.${field}`}
                    value={formData.instructor2[field] || ""}
                    onChange={handleChange}
                    readOnly={!editing}
                  />
                </React.Fragment>
              ))}

            {mentor2Enabled && (
              <>
                <label>Is Mentor 2 Ready to attend the Viva ?</label>
                <select
                  name="mentorId2ReadyViva"
                  value={formData.mentorId2ReadyViva || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  required
                >
                  <option value="">-- Select --</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="maybe">Maybe</option>
                </select>
              </>
            )}

            {editing ? (
              <>
                <button type="submit" className="btn btn-success me-2">
                  Save Changes
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setEditing(true)}
                disabled={isDisabled}
                style={{cursor:isDisabled?"not-allowed" :"pointer"}}
              >
                Edit
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ViewProjectParticular;


